import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import useTheme from 'hooks/useTheme';
import AntButton from 'components/AntButton/AntButton';
import { Modal, TextField } from 'components';
import {
	formatMaterialValidationCaseLabel,
	formatMaterialValidationValue,
} from './materialValidationPresentation';

export const MIN_COMMENT_LENGTH = 10;
export const MAX_COMMENT_LENGTH = 500;
export const MANUAL_CORRECTION_MODE = 'REQUEST_MANUAL_CORRECTION';
export const MANUAL_CORRECTION_REASON_OPTIONS = [
	{ value: 'MISSING_SERIALS', label: 'Faltan series de equipos' },
	{ value: 'INCORRECT_SERIALS', label: 'Hay series incorrectas' },
	{ value: 'MISSING_MATERIALS', label: 'Faltan materiales' },
	{
		value: 'INCORRECT_MATERIALS',
		label: 'Hay materiales o cantidades incorrectas',
	},
	{
		value: 'OTHER_MATERIAL_CORRECTION',
		label: 'Se requiere otro ajuste de materiales',
	},
];

const REASON_HELP_ID = 'material-validation-manual-reason-help';
const REASON_ERROR_ID = 'material-validation-manual-reason-error';
const COMMENT_HELP_ID = 'material-validation-decision-comment-help';
const COMMENT_COUNT_ID = 'material-validation-decision-comment-count';
const COMMENT_ERROR_ID = 'material-validation-decision-comment-error';
const CONTEXT_TITLE_ID = 'material-validation-decision-context-title';

const getDecisionThemeTokens = theme => ({
	background: theme._primaryBackgroundColor,
	surface: theme._secondaryBackgroundColor,
	elevatedSurface: theme._surfaceBackgroundColor,
	textPrimary: theme._highEmphasisColor,
	textSecondary: theme._mediumEmphasisColor,
	textInactive: theme._inactiveColor,
	border: theme._borderColor,
	accent: theme._secondaryColor,
	warning: theme._warningColor,
});

const DecisionModal = styled(Modal)`
	&& {
		max-width: calc(100vw - 32px);
	}

	@media (max-width: 768px) {
		&& {
			max-width: calc(100vw - 32px);
		}
	}

	@media (max-width: 390px) {
		&& {
			max-width: calc(100vw - 24px);
		}
	}
`;

const Form = styled.form`
	--material-decision-background: ${({ $tokens }) => $tokens.background};
	--material-decision-surface: ${({ $tokens }) => $tokens.surface};
	--material-decision-surface-elevated: ${({ $tokens }) =>
		$tokens.elevatedSurface};
	--material-decision-text: ${({ $tokens }) => $tokens.textPrimary};
	--material-decision-text-secondary: ${({ $tokens }) => $tokens.textSecondary};
	--material-decision-text-inactive: ${({ $tokens }) => $tokens.textInactive};
	--material-decision-border: ${({ $tokens }) => $tokens.border};
	--material-decision-accent: ${({ $tokens }) => $tokens.accent};
	--material-decision-warning: ${({ $tokens }) => $tokens.warning};

	background: var(--material-decision-background);
	box-sizing: border-box;
	color: var(--material-decision-text);
	width: 100%;
`;

const Content = styled.div`
	box-sizing: border-box;
	padding: 24px;

	> * + * {
		margin-top: 18px;
	}

	@media (max-width: 768px) {
		padding: 16px;
	}

	@media (max-width: 390px) {
		padding: 12px;

		> * + * {
			margin-top: 14px;
		}
	}
`;

const ContextCard = styled.section`
	background: var(--material-decision-surface);
	border: 1px solid var(--material-decision-border);
	border-left: 3px solid var(--material-decision-accent);
	border-radius: 8px;
	padding: 14px 16px;
`;

const ContextHeading = styled.h3`
	color: var(--material-decision-text);
	font-size: 15px;
	font-weight: 700;
	line-height: 1.4;
	margin: 0;
	overflow-wrap: break-word;
	word-break: break-word;
	overflow-wrap: anywhere;
`;

const ContextCopy = styled.p`
	color: var(--material-decision-text-secondary);
	font-size: 13px;
	line-height: 1.5;
	margin: 6px 0 0;
	overflow-wrap: break-word;
	word-break: break-word;
	overflow-wrap: anywhere;
`;

const AuditWarning = styled.p`
	background: var(--material-decision-surface);
	border: 1px solid var(--material-decision-warning);
	border-left: 3px solid var(--material-decision-warning);
	border-radius: 8px;
	color: var(--material-decision-text-secondary);
	font-size: 13px;
	line-height: 1.5;
	margin: 0;
	padding: 12px 14px;
	overflow-wrap: break-word;
	word-break: break-word;
	overflow-wrap: anywhere;
`;

const FieldSection = styled.section`
	> * + * {
		margin-top: 8px;
	}
`;

const ResolutionSection = styled.section`
	> * + * {
		margin-top: 12px;
	}
`;

const ResolutionHeading = styled.h3`
	color: var(--material-decision-text);
	font-size: 14px;
	margin: 0;
`;

const ResolutionHelp = styled.p`
	color: var(--material-decision-text-secondary);
	font-size: 12px;
	line-height: 1.45;
	margin: 0;
`;

const ResolutionFieldset = styled.fieldset`
	background: var(--material-decision-surface);
	border: 1px solid var(--material-decision-border);
	border-radius: 8px;
	box-sizing: border-box;
	margin: 0;
	min-width: 0;
	padding: 12px;

	> * + * {
		margin-top: 9px;
	}
`;

const ResolutionLegend = styled.legend`
	color: var(--material-decision-text);
	font-size: 13px;
	font-weight: 700;
	line-height: 1.4;
	max-width: 100%;
	padding: 0 4px;
	word-break: break-word;
	overflow-wrap: break-word;
	overflow-wrap: anywhere;
`;

const RadioOption = styled.label`
	align-items: flex-start;
	color: var(--material-decision-text);
	display: flex;
	font-size: 12px;
	line-height: 1.45;

	input {
		flex: 0 0 auto;
		margin: 2px 8px 0 0;
	}

	input:focus {
		outline: 2px solid var(--material-decision-accent);
		outline-offset: 2px;
	}
`;

const AdjustedFields = styled.div`
	border-left: 3px solid var(--material-decision-accent);
	padding-left: 12px;

	> * + * {
		margin-top: 8px;
	}
`;

const NumberInput = styled.input`
	background: var(--material-decision-surface-elevated);
	border: 1px solid var(--material-decision-border);
	border-radius: 8px;
	box-sizing: border-box;
	color: var(--material-decision-text);
	font: inherit;
	min-height: 40px;
	padding: 8px 10px;
	width: 100%;

	&:focus {
		border-color: var(--material-decision-accent);
		outline: 2px solid var(--material-decision-accent);
		outline-offset: 2px;
	}

	&[aria-invalid='true'] {
		border-color: var(--material-decision-warning);
	}

	&[disabled] {
		color: var(--material-decision-text-inactive);
	}
`;

const ResolutionPreview = styled.p`
	color: var(--material-decision-text-secondary);
	font-size: 12px;
	font-variant-numeric: tabular-nums;
	margin: 0;

	> span {
		display: block;
	}

	> span + span {
		margin-top: 3px;
	}
`;

const Label = styled.label`
	color: var(--material-decision-text);
	display: block;
	font-size: 13px;
	font-weight: 700;
`;

const Help = styled.p`
	color: var(--material-decision-text-secondary);
	font-size: 12px;
	line-height: 1.45;
	margin: 0;
`;

const ReasonSelect = styled.select`
	appearance: none;
	background: var(--material-decision-surface);
	border: 1px solid var(--material-decision-border);
	border-radius: 8px;
	box-sizing: border-box;
	color: var(--material-decision-text);
	font: inherit;
	line-height: 1.5;
	min-height: 42px;
	padding: 9px 12px;
	width: 100%;

	&:hover {
		border-color: var(--material-decision-accent);
		filter: brightness(1.04);
	}

	&:focus {
		border-color: var(--material-decision-accent);
		box-shadow: none;
		filter: none;
		outline: 2px solid var(--material-decision-accent);
		outline-offset: 2px;
	}

	&[aria-invalid='true'] {
		border-color: var(--material-decision-warning);
	}

	&[disabled] {
		background: var(--material-decision-surface-elevated);
		border-color: var(--material-decision-border);
		color: var(--material-decision-text-inactive);
		filter: none;
	}
`;

const CommentTextArea = styled(TextField.TextArea)`
	&&.ant-input {
		background: var(--material-decision-surface);
		border: 1px solid var(--material-decision-border);
		border-radius: 8px;
		box-sizing: border-box;
		color: var(--material-decision-text);
		height: auto;
		line-height: 1.5;
		padding: 10px 12px;
		resize: vertical;
		width: 100%;
	}

	&&.ant-input:hover {
		border-color: var(--material-decision-accent);
		filter: brightness(1.04);
	}

	&&.ant-input:focus {
		border-color: var(--material-decision-accent);
		box-shadow: none;
		filter: none;
		outline: 2px solid var(--material-decision-accent);
		outline-offset: 2px;
	}

	&&.ant-input[aria-invalid='true'] {
		border-color: var(--material-decision-warning);
	}

	&&.ant-input[aria-invalid='true']:hover {
		border-color: var(--material-decision-warning);
	}

	&&.ant-input[aria-invalid='true']:focus {
		border-color: var(--material-decision-warning);
		outline: 2px solid var(--material-decision-accent);
		outline-offset: 2px;
	}

	&&.ant-input[disabled] {
		background: var(--material-decision-surface-elevated);
		border-color: var(--material-decision-border);
		color: var(--material-decision-text-inactive);
		filter: none;
	}
`;

const FieldMeta = styled.div`
	align-items: flex-start;
	display: flex;
	justify-content: flex-end;

	> * + * {
		margin-left: 12px;
	}
`;

const ErrorMessage = styled.span`
	border-left: 3px solid var(--material-decision-warning);
	color: var(--material-decision-text);
	flex: 1 1 auto;
	font-size: 12px;
	line-height: 1.45;
	padding-left: 8px;
`;

const SubmissionError = styled.p`
	background: var(--material-decision-surface);
	border-left: 3px solid var(--material-decision-warning);
	border-radius: 6px;
	color: var(--material-decision-text);
	font-size: 12px;
	line-height: 1.45;
	margin: 0;
	padding: 9px 10px;
`;

const CharacterCount = styled.span`
	color: var(--material-decision-text-secondary);
	flex: 0 0 auto;
	font-size: 12px;
	font-variant-numeric: tabular-nums;
	line-height: 1.45;
`;

const Footer = styled.div`
	align-items: center;
	background: var(--material-decision-background);
	border-top: 1px solid var(--material-decision-border);
	display: flex;
	justify-content: flex-end;
	padding: 16px 24px 20px;

	> button + button {
		margin-left: 10px;
	}

	@media (max-width: 768px) {
		padding: 14px 16px 16px;
	}

	@media (max-width: 390px) {
		align-items: stretch;
		flex-direction: column;
		padding: 12px;

		> button {
			width: 100%;
		}

		> button + button {
			margin-left: 0;
			margin-top: 8px;
		}
	}
`;

const ActionButton = styled(AntButton)`
	&& {
		background: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--material-decision-accent)'
				: 'var(--material-decision-surface)'};
		border-color: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--material-decision-accent)'
				: 'var(--material-decision-border)'};
		border-radius: 8px;
		box-shadow: none;
		color: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--material-decision-background)'
				: 'var(--material-decision-text)'};
		font-weight: 700;
		min-height: 40px;
		min-width: 108px;
		padding: 0 18px;
		transition:
			background-color 120ms ease,
			border-color 120ms ease,
			color 120ms ease,
			filter 120ms ease;
	}

	&&:hover {
		background: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--material-decision-accent)'
				: 'var(--material-decision-surface-elevated)'};
		border-color: var(--material-decision-accent);
		color: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--material-decision-background)'
				: 'var(--material-decision-accent)'};
		filter: brightness(1.08);
	}

	&&:focus {
		border-color: var(--material-decision-accent);
		outline: 2px solid var(--material-decision-accent);
		outline-offset: 2px;
	}

	&&[disabled] {
		background: var(--material-decision-surface-elevated);
		border-color: var(--material-decision-border);
		color: var(--material-decision-text-inactive);
		cursor: not-allowed;
		filter: none;
		opacity: 1;
	}

	&&.ant-btn-loading,
	&&.ant-btn-loading[disabled] {
		background: var(--material-decision-accent);
		border-color: var(--material-decision-accent);
		color: var(--material-decision-background);
		filter: none;
		opacity: 0.82;
	}
`;

const normalizeMetricKey = value => String(value);
const safeIdPart = value =>
	normalizeMetricKey(value).replace(/[^a-zA-Z0-9_-]/g, '-');

const findComparison = (comparisons, metricKey) =>
	(comparisons || []).find(
		comparison =>
			normalizeMetricKey(comparison.metricKey) ===
			normalizeMetricKey(metricKey),
	);

const isValidAdjustedValue = (value, constraints) => {
	const normalized = String(value ?? '').trim();
	const decimalPlaces = constraints?.decimalPlaces;
	if (!Number.isSafeInteger(decimalPlaces)) return false;
	const pattern = new RegExp(
		decimalPlaces === 0
			? '^(?:0|[1-9]\\d*)$'
			: `^(?:0|[1-9]\\d*)(?:\\.\\d{1,${decimalPlaces}})?$`,
	);
	if (!pattern.test(normalized)) return false;
	const numericValue = Number(normalized);
	if (!Number.isFinite(numericValue) || numericValue < constraints.minValue)
		return false;
	if (
		constraints.maxValue !== undefined &&
		constraints.maxValue !== null &&
		numericValue > constraints.maxValue
	)
		return false;
	return true;
};

const getResolutionItemValidity = (item, value) => {
	if (!value || !item.strategies.includes(value.strategy)) return false;
	if (value.strategy !== 'PAY_ADJUSTED') return true;
	const justification = String(value.justification || '').trim();
	return (
		isValidAdjustedValue(value.adjustedValue, item.adjustedValueConstraints) &&
		justification.length >= MIN_COMMENT_LENGTH &&
		justification.length <= MAX_COMMENT_LENGTH
	);
};

const getQuantityPreview = (item, comparison, selection) => {
	if (!comparison || !selection) return undefined;
	if (selection.strategy === 'PAY_ADJUSTED')
		return { selectedValue: selection.adjustedValue, adjusted: true };
	const authoritativePreview = item.previews?.[selection.strategy];
	if (authoritativePreview)
		return {
			selectedValue: authoritativePreview.selectedValue,
			resolvedValue: authoritativePreview.resolvedValue,
			authoritative: true,
		};
	if (selection.strategy === 'PAY_EXPECTED')
		return { selectedValue: comparison.expectedValue };
	if (selection.strategy === 'PAY_REPORTED')
		return { selectedValue: comparison.reportedValue };
	return undefined;
};

export default function MaterialValidationDecisionModal({
	mode,
	materialCase,
	isMatchingCorrection = false,
	isMatchingValidation = false,
	inventoryAuditAvailable,
	comparisons = [],
	approvalQuantitySelection,
	evidenceHash,
	isSubmitting = false,
	submissionError,
	onCancel,
	onSubmit,
}) {
	const { theme, themeProfile } = useTheme();
	const themeTokens = getDecisionThemeTokens(theme);
	const [reasonCode, setReasonCode] = useState('');
	const [reasonTouched, setReasonTouched] = useState(false);
	const [comment, setComment] = useState('');
	const [commentTouched, setCommentTouched] = useState(false);
	const [submitAttempted, setSubmitAttempted] = useState(false);
	const [pricingSelections, setPricingSelections] = useState({});
	const normalizedComment = comment.trim();
	const isManualRequest = mode === MANUAL_CORRECTION_MODE;
	const isReturn = mode === 'RETURN_FOR_CORRECTION';
	const requiresPricingResolution =
		mode === 'APPROVE' && approvalQuantitySelection?.required === true;
	const pricingItems = requiresPricingResolution
		? approvalQuantitySelection.items || []
		: [];
	const invalidPricingResolution = pricingItems.some(
		item =>
			!getResolutionItemValidity(
				item,
				pricingSelections[normalizeMetricKey(item.metricKey)],
			),
	);
	const invalidReason =
		isManualRequest &&
		!MANUAL_CORRECTION_REASON_OPTIONS.some(
			option => option.value === reasonCode,
		);
	const showReasonError = invalidReason && (reasonTouched || submitAttempted);
	const commentTooShort = normalizedComment.length < MIN_COMMENT_LENGTH;
	const commentTooLong = normalizedComment.length > MAX_COMMENT_LENGTH;
	const invalidComment = commentTooShort || commentTooLong;
	const showCommentError =
		invalidComment && (commentTouched || submitAttempted);
	const title = isManualRequest
		? 'Habilitar corrección de materiales'
		: isReturn
			? 'Solicitar corrección'
			: isMatchingCorrection
				? 'Confirmar corrección de materiales'
				: isMatchingValidation
					? 'Confirmar validación de materiales'
					: 'Aprobar excepción de materiales';
	const contextDescription = isManualRequest
		? 'El técnico podrá ajustar únicamente el inventario. La orden, la facturación y los pagos permanecerán cerrados.'
		: isReturn
			? 'El caso quedará marcado para corrección según el flujo configurado.'
			: isMatchingCorrection
				? 'Confirme que revisó la validación actual y que la corrección del técnico coincide con el inventario reportado.'
				: isMatchingValidation
					? 'Confirme el resultado de la validación actual. La disponibilidad de la auditoría se indica a continuación.'
					: 'Confirme que revisó la evidencia del caso vigente y acepta la excepción.';
	const auditWarning =
		inventoryAuditAvailable === false
			? 'La auditoría de cambios no está disponible. Confirme la validación sin asumir que existe evidencia de una corrección.'
			: 'Esta respuesta no incluye auditoría de cambios. Confirme la validación sin asumir que existe evidencia de una corrección.';
	const contextHeading = isManualRequest
		? 'Ajuste excepcional de inventario'
		: materialCase
			? formatMaterialValidationCaseLabel(materialCase)
			: 'Caso vigente de validación de materiales';
	const errorMessage = commentTooShort
		? `El comentario tiene un mínimo de ${MIN_COMMENT_LENGTH} caracteres.`
		: `El comentario tiene un máximo de ${MAX_COMMENT_LENGTH} caracteres.`;
	const commentDescribedBy = [
		COMMENT_HELP_ID,
		COMMENT_COUNT_ID,
		showCommentError ? COMMENT_ERROR_ID : undefined,
	]
		.filter(Boolean)
		.join(' ');

	useEffect(() => {
		setReasonCode('');
		setReasonTouched(false);
		setComment('');
		setCommentTouched(false);
		setSubmitAttempted(false);
		setPricingSelections({});
	}, [
		mode,
		materialCase?.id,
		materialCase?.version,
		evidenceHash,
		approvalQuantitySelection,
	]);

	const updatePricingSelection = (metricKey, patch) => {
		const key = normalizeMetricKey(metricKey);
		setPricingSelections(current => ({
			...current,
			[key]: { ...(current[key] || {}), ...patch },
		}));
	};

	const close = () => {
		if (isSubmitting) return;
		setReasonCode('');
		setReasonTouched(false);
		setComment('');
		setCommentTouched(false);
		setSubmitAttempted(false);
		setPricingSelections({});
		if (onCancel) onCancel();
	};

	const submit = async event => {
		event.preventDefault();
		setSubmitAttempted(true);
		if (
			!mode ||
			invalidReason ||
			invalidComment ||
			invalidPricingResolution ||
			isSubmitting ||
			!onSubmit
		)
			return;
		const pricingResolution = requiresPricingResolution
			? {
					pricingPolicySha256: approvalQuantitySelection.pricingPolicySha256,
					selections: pricingItems.map(item => {
						const selection =
							pricingSelections[normalizeMetricKey(item.metricKey)];
						return {
							metricKey: item.metricKey,
							strategy: selection.strategy,
							...(selection.strategy === 'PAY_ADJUSTED'
								? {
										adjustedValue: String(selection.adjustedValue ?? '').trim(),
										justification: String(selection.justification).trim(),
									}
								: {}),
						};
					}),
				}
			: undefined;
		try {
			return await onSubmit(
				isManualRequest
					? { reasonCode, comment: normalizedComment }
					: {
							decision: mode,
							comment: normalizedComment,
							...(pricingResolution ? { pricingResolution } : {}),
						},
			);
		} catch (_error) {
			// The hook owns and renders decision errors. This event boundary only
			// consumes the rejection so React cannot surface it as unhandled.
			return undefined;
		}
	};

	return (
		<DecisionModal
			visible={!!mode}
			title={title}
			aria-label={title}
			footer={null}
			width={480}
			destroyOnClose
			onCancel={close}
			closable={!isSubmitting}
			maskClosable={!isSubmitting}
			keyboard={!isSubmitting}
		>
			<Form
				data-testid='material-validation-decision-modal'
				data-theme={themeProfile}
				$tokens={themeTokens}
				onSubmit={submit}
				noValidate
				aria-busy={isSubmitting}
			>
				<Content>
					<ContextCard
						data-testid='decision-context'
						aria-labelledby={CONTEXT_TITLE_ID}
					>
						<ContextHeading id={CONTEXT_TITLE_ID}>
							{contextHeading}
						</ContextHeading>
						<ContextCopy>{contextDescription}</ContextCopy>
					</ContextCard>
					{isMatchingValidation && (
						<AuditWarning
							data-testid='material-validation-audit-warning'
							role='status'
						>
							{auditWarning}
						</AuditWarning>
					)}

					{isManualRequest && (
						<FieldSection aria-labelledby='material-validation-manual-reason-label'>
							<Label
								id='material-validation-manual-reason-label'
								htmlFor='material-validation-manual-reason'
							>
								Motivo (requerido)
							</Label>
							<Help id={REASON_HELP_ID}>
								Seleccione la situación que debe corregir el técnico.
							</Help>
							<ReasonSelect
								id='material-validation-manual-reason'
								value={reasonCode}
								onChange={event => setReasonCode(event.target.value)}
								onBlur={() => setReasonTouched(true)}
								disabled={isSubmitting}
								required
								aria-required='true'
								aria-invalid={showReasonError || undefined}
								aria-describedby={`${REASON_HELP_ID}${
									showReasonError ? ` ${REASON_ERROR_ID}` : ''
								}`}
								autoFocus
							>
								<option value=''>Seleccione un motivo</option>
								{MANUAL_CORRECTION_REASON_OPTIONS.map(option => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</ReasonSelect>
							{showReasonError && (
								<ErrorMessage id={REASON_ERROR_ID} role='alert'>
									Seleccione un motivo para continuar.
								</ErrorMessage>
							)}
						</FieldSection>
					)}

					{requiresPricingResolution && (
						<ResolutionSection aria-labelledby='material-validation-pricing-resolution-title'>
							<ResolutionHeading id='material-validation-pricing-resolution-title'>
								Cantidad que se pagará por material
							</ResolutionHeading>
							<ResolutionHelp>
								Seleccione una opción para cada diferencia. Synapse resolverá la
								cantidad final en el servidor.
							</ResolutionHelp>
							{pricingItems.map((item, itemIndex) => {
								const comparison = findComparison(comparisons, item.metricKey);
								const key = normalizeMetricKey(item.metricKey);
								const idPart = `${itemIndex}-${safeIdPart(item.metricKey)}`;
								const selection = pricingSelections[key] || {};
								const constraints = item.adjustedValueConstraints;
								const adjustedSelected = selection.strategy === 'PAY_ADJUSTED';
								const invalidAdjusted =
									adjustedSelected &&
									!isValidAdjustedValue(selection.adjustedValue, constraints);
								const normalizedJustification = String(
									selection.justification || '',
								).trim();
								const invalidJustification =
									adjustedSelected &&
									(normalizedJustification.length < MIN_COMMENT_LENGTH ||
										normalizedJustification.length > MAX_COMMENT_LENGTH);
								const quantityPreview = getQuantityPreview(
									item,
									comparison,
									selection,
								);
								const previewWasConstrained =
									quantityPreview?.authoritative &&
									String(quantityPreview.selectedValue) !==
										String(quantityPreview.resolvedValue);
								const subject =
									comparison?.subject?.label ||
									comparison?.subject?.code ||
									item.metricKey;
								const helpId = `material-pricing-${idPart}-help`;
								const errorId = `material-pricing-${idPart}-error`;
								return (
									<ResolutionFieldset
										key={key}
										data-testid={`pricing-resolution-${idPart}`}
									>
										<ResolutionLegend>{subject}</ResolutionLegend>
										<ResolutionHelp id={helpId}>
											Elija la cantidad que debe usar el recálculo.
										</ResolutionHelp>
										{item.strategies.includes('PAY_EXPECTED') && (
											<RadioOption>
												<input
													type='radio'
													name={`pricing-resolution-${idPart}`}
													value='PAY_EXPECTED'
													checked={selection.strategy === 'PAY_EXPECTED'}
													onChange={() =>
														updatePricingSelection(item.metricKey, {
															strategy: 'PAY_EXPECTED',
														})
													}
													disabled={isSubmitting}
													required
													autoFocus={itemIndex === 0}
													aria-describedby={helpId}
												/>
												<span>
													Pagar esperado —{' '}
													{formatMaterialValidationValue(
														comparison.expectedValue,
														item.unit,
													)}
												</span>
											</RadioOption>
										)}
										{item.strategies.includes('PAY_REPORTED') && (
											<RadioOption>
												<input
													type='radio'
													name={`pricing-resolution-${idPart}`}
													value='PAY_REPORTED'
													checked={selection.strategy === 'PAY_REPORTED'}
													onChange={() =>
														updatePricingSelection(item.metricKey, {
															strategy: 'PAY_REPORTED',
														})
													}
													disabled={isSubmitting}
													aria-describedby={helpId}
												/>
												<span>
													Pagar reportado/calculado Synapse —{' '}
													{formatMaterialValidationValue(
														comparison.reportedValue,
														item.unit,
													)}
												</span>
											</RadioOption>
										)}
										{item.strategies.includes('PAY_ADJUSTED') && (
											<RadioOption>
												<input
													type='radio'
													name={`pricing-resolution-${idPart}`}
													value='PAY_ADJUSTED'
													checked={adjustedSelected}
													onChange={() =>
														updatePricingSelection(item.metricKey, {
															strategy: 'PAY_ADJUSTED',
														})
													}
													disabled={isSubmitting}
													aria-describedby={helpId}
												/>
												<span>Cantidad ajustada</span>
											</RadioOption>
										)}
										{adjustedSelected && (
											<AdjustedFields>
												<Label htmlFor={`material-pricing-${idPart}-value`}>
													Cantidad ajustada (requerida)
												</Label>
												<NumberInput
													id={`material-pricing-${idPart}-value`}
													type='number'
													min={constraints.minValue}
													max={constraints.maxValue ?? undefined}
													step={
														constraints.decimalPlaces === 0
															? '1'
															: `0.${'0'.repeat(
																	constraints.decimalPlaces - 1,
																)}1`
													}
													value={selection.adjustedValue ?? ''}
													onChange={event =>
														updatePricingSelection(item.metricKey, {
															adjustedValue: event.target.value,
														})
													}
													disabled={isSubmitting}
													required
													aria-invalid={
														invalidAdjusted && submitAttempted
															? 'true'
															: undefined
													}
													aria-describedby={`${helpId}${
														submitAttempted && invalidAdjusted
															? ` ${errorId}`
															: ''
													}`}
												/>
												<Label
													htmlFor={`material-pricing-${idPart}-justification`}
												>
													Justificación del ajuste (requerida)
												</Label>
												<CommentTextArea
													id={`material-pricing-${idPart}-justification`}
													value={selection.justification || ''}
													onChange={event =>
														updatePricingSelection(item.metricKey, {
															justification: event.target.value,
														})
													}
													disabled={isSubmitting}
													required
													rows={2}
													height='auto'
													aria-invalid={
														invalidJustification && submitAttempted
															? 'true'
															: undefined
													}
													aria-describedby={`${helpId}${
														submitAttempted && invalidJustification
															? ` ${errorId}`
															: ''
													}`}
												/>
												{submitAttempted &&
													(invalidAdjusted || invalidJustification) && (
														<ErrorMessage id={errorId} role='alert'>
															Ingrese una cantidad permitida y una justificación
															entre {MIN_COMMENT_LENGTH} y {MAX_COMMENT_LENGTH}{' '}
															caracteres.
														</ErrorMessage>
													)}
											</AdjustedFields>
										)}
										{quantityPreview !== undefined &&
											(selection.strategy !== 'PAY_ADJUSTED' ||
												!invalidAdjusted) && (
												<ResolutionPreview aria-live='polite'>
													{quantityPreview.adjusted ? (
														<>
															<span>
																Cantidad solicitada:{' '}
																{formatMaterialValidationValue(
																	quantityPreview.selectedValue,
																	item.unit,
																)}
															</span>
															<span>
																La cantidad que se pagará será confirmada por el
																servidor y puede cambiar por topes o reglas
																aplicables.
															</span>
														</>
													) : quantityPreview.authoritative ? (
														<>
															{previewWasConstrained && (
																<span>
																	Cantidad seleccionada:{' '}
																	{formatMaterialValidationValue(
																		quantityPreview.selectedValue,
																		item.unit,
																	)}
																</span>
															)}
															<span>
																<strong>Cantidad que se pagará:</strong>{' '}
																{formatMaterialValidationValue(
																	quantityPreview.resolvedValue,
																	item.unit,
																)}
															</span>
															{previewWasConstrained && (
																<span>
																	Synapse aplicó un tope o regla autoritativa a
																	esta cantidad.
																</span>
															)}
														</>
													) : (
														<span>
															Cantidad seleccionada:{' '}
															{formatMaterialValidationValue(
																quantityPreview.selectedValue,
																item.unit,
															)}
														</span>
													)}
												</ResolutionPreview>
											)}
									</ResolutionFieldset>
								);
							})}
						</ResolutionSection>
					)}

					<FieldSection aria-labelledby='material-validation-decision-comment-label'>
						<Label
							id='material-validation-decision-comment-label'
							htmlFor='material-validation-decision-comment'
						>
							Comentario (requerido)
						</Label>
						<Help id={COMMENT_HELP_ID}>
							Use entre {MIN_COMMENT_LENGTH} y {MAX_COMMENT_LENGTH} caracteres.
						</Help>
						<CommentTextArea
							id='material-validation-decision-comment'
							value={comment}
							onChange={event => setComment(event.target.value)}
							onBlur={() => setCommentTouched(true)}
							disabled={isSubmitting}
							required
							aria-required='true'
							aria-invalid={showCommentError || undefined}
							aria-describedby={commentDescribedBy}
							placeholder={
								isManualRequest
									? 'Describa qué debe corregir el técnico'
									: 'Describa el motivo de la decisión'
							}
							autoFocus={!isManualRequest && !requiresPricingResolution}
							rows={3}
							height='auto'
						/>
						<FieldMeta>
							{showCommentError && (
								<ErrorMessage id={COMMENT_ERROR_ID} role='alert'>
									{errorMessage}
								</ErrorMessage>
							)}
							<CharacterCount id={COMMENT_COUNT_ID}>
								{normalizedComment.length}/{MAX_COMMENT_LENGTH} caracteres
							</CharacterCount>
						</FieldMeta>
					</FieldSection>
					{submissionError && (
						<SubmissionError role='alert'>{submissionError}</SubmissionError>
					)}
				</Content>

				<Footer aria-label='Acciones de decisión'>
					<ActionButton
						$intent='secondary'
						data-intent='secondary'
						data-action='cancel'
						htmlType='button'
						onClick={close}
						disabled={isSubmitting}
					>
						Cancelar
					</ActionButton>
					<ActionButton
						$intent='primary'
						data-intent='primary'
						data-action='confirm'
						type='primary'
						htmlType='submit'
						loading={isSubmitting}
						disabled={
							invalidReason ||
							invalidComment ||
							invalidPricingResolution ||
							isSubmitting
						}
					>
						{requiresPricingResolution ? 'Aprobar y recalcular' : 'Confirmar'}
					</ActionButton>
				</Footer>
			</Form>
		</DecisionModal>
	);
}
